import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { sessionStorageKeys } from '@/utils/tokens';
import { JourneyData, StopInfo } from '@/apps/dispatcher/types/ISchedule';
import { BATCH_CREATE_MAX_ITEMS } from '@/utils/configs/dispatcherJournalConfig';

interface JourneyState {
  journeyItems: JourneyData[];
  activeJourneyItems: JourneyData[];
  loading: boolean;
  journeyStopsLoading?: boolean;
  error: string | null;
  scheduleVersion: string | null;
  scheduleVersionSync: boolean | null;
  isScheduleUpdateAvailable: boolean;
  isScheduleUpdateRequired: boolean;
  isScheduleUpdated: boolean;
}

const defaultJourneyState: JourneyState = {
  journeyItems: [],
  activeJourneyItems: [],
  loading: false,
  journeyStopsLoading: false,
  error: null,
  scheduleVersion: null,
  scheduleVersionSync: null,
  isScheduleUpdateAvailable: false,
  isScheduleUpdateRequired: false,
  isScheduleUpdated: false,
};

const initialState: JourneyState = {
  ...defaultJourneyState,
  scheduleVersion: JSON.parse(
    sessionStorage.getItem(sessionStorageKeys.scheduleVersion) || 'null'
  ),
};

const journeyStateSlice = createSlice({
  name: 'journeyItems',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setJourneyStopsLoading(state, action: PayloadAction<boolean>) {
      state.journeyStopsLoading = action.payload;
    },
    setJourneyItems(state, action: PayloadAction<JourneyData[]>) {
      const existingJourneyIds = new Map(
        state.journeyItems.map((journey) => [journey.id, true])
      );

      const uniqueNewJourneys = action.payload.filter(
        (journey) => !existingJourneyIds.has(journey.id)
      );

      state.journeyItems = [...state.journeyItems, ...uniqueNewJourneys];
      state.loading = false;
      state.error = null;
    },
    updateJourneyItem(state, action: PayloadAction<JourneyData>) {
      const index = state.journeyItems.findIndex(
        (item) => item.id === action.payload.id
      );
      if (index !== -1) {
        state.journeyItems[index] = action.payload;
      }
    },
    updateJourneyStops(
      state,
      action: PayloadAction<{ id: number | string; stops: StopInfo[] }>
    ) {
      const index = state.journeyItems.findIndex(
        (item) => item.id === action.payload.id
      );
      if (index !== -1 && state.journeyItems[index]) {
        state.journeyItems[index].stopOptions = action.payload.stops;
      }
    },
    setActiveJourneyItems(state, action: PayloadAction<JourneyData[]>) {
      state.activeJourneyItems = action.payload;
    },
    addActiveJourney(state, action: PayloadAction<number | string>) {
      const journey = state.journeyItems.find(
        (item) => item.id === action.payload
      );
      // Add it if not already in activeJourneyItems and under the batch limit
      if (
        journey &&
        !state.activeJourneyItems.find((item) => item.id === journey.id) &&
        state.activeJourneyItems.length < BATCH_CREATE_MAX_ITEMS
      ) {
        state.activeJourneyItems.push(journey);
      }
    },
    removeActiveJourney(state, action: PayloadAction<number | string>) {
      state.activeJourneyItems = state.activeJourneyItems.filter(
        (item) => item.id !== action.payload
      );
    },
    updateActiveJourneyItems(
      state,
      action: PayloadAction<Partial<JourneyData>>
    ) {
      state.activeJourneyItems = state.activeJourneyItems.map((item) => ({
        ...item,
        ...action.payload,
      }));
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.loading = false;
    },
    setScheduleVersion(state, action: PayloadAction<string | null>) {
      state.scheduleVersion = action.payload;
      sessionStorage.setItem(
        sessionStorageKeys.scheduleVersion,
        JSON.stringify(action.payload)
      );
    },
    setScheduleVersionSync(state, action: PayloadAction<boolean>) {
      state.scheduleVersionSync = action.payload;
    },
    setIsScheduleUpdateAvailable(state, action: PayloadAction<boolean>) {
      state.isScheduleUpdateAvailable = action.payload;
    },
    setIsScheduleUpdateRequired(state, action: PayloadAction<boolean>) {
      state.isScheduleUpdateRequired = action.payload;
    },
    setIsScheduleUpdated(state, action: PayloadAction<boolean>) {
      state.isScheduleUpdated = action.payload;
    },
  },
});

export const {
  setLoading,
  setJourneyStopsLoading,
  setJourneyItems,
  setActiveJourneyItems,
  addActiveJourney,
  removeActiveJourney,
  updateActiveJourneyItems,
  setError,
  updateJourneyItem,
  updateJourneyStops,
  setScheduleVersion,
  setScheduleVersionSync,
  setIsScheduleUpdated,
  setIsScheduleUpdateAvailable,
  setIsScheduleUpdateRequired,
} = journeyStateSlice.actions;

export default journeyStateSlice.reducer;
